import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useConversations, useMessages, useSendMessage } from '@/hooks/useMessages';
import { useAuth } from '@/contexts/AuthContext';
import { useProfiles } from '@/hooks/useProfile';
import { Loader2, Send, MessageCircle, ArrowLeft, Lock, Plus, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useMembershipContext } from '@/components/layout/AppLayout';

export default function MessagesPage() {
  const [searchParams] = useSearchParams();
  const initialRecipient = searchParams.get('to');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(initialRecipient);
  const [messageText, setMessageText] = useState('');
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();
  const { isPending } = useMembershipContext();
  const {
    data: conversations,
    isLoading: loadingConversations,
    isError: conversationsError,
    error: conversationsErrorObj,
  } = useConversations();
  const {
    data: messages,
    isLoading: loadingMessages,
    isError: messagesError,
    error: messagesErrorObj,
  } = useMessages(selectedUserId || '');
  const { data: profiles } = useProfiles();
  const sendMessage = useSendMessage();

  // Filter profiles for new message dialog (exclude self)
  const availableMembers = profiles?.filter(
    (p) => p.user_id !== user?.id &&
    (memberSearch === '' ||
      p.full_name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
      p.company?.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  if (isPending) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ height: 'calc(100dvh - 10rem)', minHeight: '400px' }}>
        <Lock className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h2 className="text-lg font-semibold text-foreground mb-2">Messaging Locked</h2>
        <p className="text-muted-foreground text-sm text-center max-w-sm">
          Messaging is available after completing your profile setup.
        </p>
      </div>
    );
  }

  // Get selected user's profile
  const selectedProfile = profiles?.find(p => p.user_id === selectedUserId);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!messageText.trim() || !selectedUserId) return;
    
    await sendMessage.mutateAsync({
      receiverId: selectedUserId,
      content: messageText.trim(),
    });
    setMessageText('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectMember = (userId: string) => {
    setSelectedUserId(userId);
    setNewMessageOpen(false);
    setMemberSearch('');
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 10rem)', minHeight: '400px' }}>
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-2xl font-display font-bold text-foreground">Messages</h1>
        <p className="text-muted-foreground text-sm">
          Direct conversations with your network
        </p>
      </div>

      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Conversations List - Hidden on mobile when chat is open */}
        <Card className={`card-qbn w-full lg:w-80 flex-shrink-0 flex flex-col ${selectedUserId ? 'hidden lg:flex' : 'flex'}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Conversations</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setNewMessageOpen(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 p-2 overflow-hidden">
            {loadingConversations ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : conversationsError ? (
              <div className="text-center py-8">
                <p className="text-sm text-destructive">
                  {conversationsErrorObj instanceof Error ? conversationsErrorObj.message : 'Unable to load conversations.'}
                </p>
              </div>
            ) : conversations && conversations.length > 0 ? (
              <ScrollArea className="h-full">
                <div className="space-y-1">
                  {conversations.map((conv) => (
                    <button
                      key={conv.partnerId}
                      onClick={() => setSelectedUserId(conv.partnerId)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                        selectedUserId === conv.partnerId
                          ? 'bg-primary/10'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <Avatar className="h-10 w-10">
                        <SignedAvatarImage src={conv.partner?.avatar_url || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {conv.partner?.full_name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm truncate">
                            {conv.partner?.full_name}
                          </p>
                          {conv.unreadCount > 0 && (
                            <span className="w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {conv.lastMessage?.content}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8">
                <MessageCircle className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No conversations yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Area - Desktop */}
        <Card className={`card-qbn flex-1 flex-col min-h-0 hidden lg:flex`}>
          {selectedUserId ? (
            <>
              {/* Chat Header */}
              <CardHeader className="pb-2 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <SignedAvatarImage src={selectedProfile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {selectedProfile?.full_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedProfile?.full_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedProfile?.company}
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-4 overflow-hidden">
                {loadingMessages ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : messagesError ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-destructive">
                      {messagesErrorObj instanceof Error ? messagesErrorObj.message : 'Unable to load messages.'}
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="h-full pr-4">
                    <div className="space-y-4">
                      {messages?.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                              msg.sender_id === user?.id
                                ? 'bg-primary text-primary-foreground rounded-br-md'
                                : 'bg-muted rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm break-words">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user?.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                            }`}>
                              {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                )}
              </CardContent>

              {/* Message Input */}
              <div className="p-4 border-t border-border flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!messageText.trim() || sendMessage.isPending}
                    className="btn-qbn-primary"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Select a conversation to start messaging
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Mobile Chat View - Fixed overlay */}
      {selectedUserId && (
        <div className="fixed inset-0 z-30 bg-background flex flex-col lg:hidden" style={{ top: '48px', bottom: '64px' }}>
          {/* Chat Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSelectedUserId(null)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Avatar className="h-9 w-9">
              <SignedAvatarImage src={selectedProfile?.avatar_url || undefined} />
              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                {selectedProfile?.full_name?.charAt(0) || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{selectedProfile?.full_name || 'Unknown'}</p>
              <p className="text-xs text-muted-foreground truncate">
                {selectedProfile?.company}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4" style={{ overscrollBehavior: 'contain' }}>
            {loadingMessages ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : messagesError ? (
              <div className="text-center py-8">
                <p className="text-sm text-destructive">
                  {messagesErrorObj instanceof Error ? messagesErrorObj.message : 'Unable to load messages.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.sender_id === user?.id
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-muted rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm break-words">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_id === user?.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="p-3 border-t border-border bg-card flex-shrink-0">
            <div className="flex gap-2">
              <Input
                placeholder="Type a message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                disabled={!messageText.trim() || sendMessage.isPending}
                size="icon"
                className="btn-qbn-primary"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Message Dialog */}
      <Dialog open={newMessageOpen} onOpenChange={setNewMessageOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <ScrollArea className="h-64">
              <div className="space-y-1">
                {availableMembers?.map((member) => (
                  <button
                    key={member.user_id}
                    onClick={() => handleSelectMember(member.user_id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <Avatar className="h-10 w-10">
                      <SignedAvatarImage src={member.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {member.full_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{member.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member.company}</p>
                    </div>
                  </button>
                ))}
                {availableMembers?.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No members found
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
